import React, { RefObject, useState } from 'react';
import ChartCard from './ChartCard';
import styles from '../../../../styles/PerformanceDashboard.module.css';
import { UserPerformanceData } from '../types';

interface ChartsSectionProps {
  taskTrendChartRef: RefObject<HTMLCanvasElement | null>;
  statusChartRef: RefObject<HTMLCanvasElement | null>;
  taskRatioChartRef: RefObject<HTMLCanvasElement | null>;
  pointsChartRef: RefObject<HTMLCanvasElement | null>;
  performanceData: UserPerformanceData | null;
  onChartSelectionChange: (chartId: string, selection: string) => void;
  chartSelections: {
    taskTrend: string;
    taskRatio: string;
  };
}

const ChartsSection: React.FC<ChartsSectionProps> = ({
  taskTrendChartRef,
  statusChartRef,
  taskRatioChartRef,
  pointsChartRef,
  performanceData,
  onChartSelectionChange,
  chartSelections,
}) => {
  // Task Trend options - support 2, 3, or 4 statuses at once
  const taskTrendOptions = [
    { value: 'completed-inprogress', label: 'Completed vs In Progress' },
    { value: 'completed-pending', label: 'Completed vs Pending' },
    { value: 'inprogress-pending', label: 'In Progress vs Pending' },
    { value: 'completed-cancelled', label: 'Completed vs Cancelled' },
    { value: 'completed-inprogress-pending', label: 'Completed vs In Progress vs Pending' },
    { value: 'completed-inprogress-pending-cancelled', label: 'Completed vs In Progress vs Pending vs Cancelled' },
  ];

  // Task Ratio options - support 2, 3, or 4 statuses at once
  const taskRatioOptions = [
    { value: 'completed-inprogress', label: 'Completed vs In Progress' },
    { value: 'completed-pending', label: 'Completed vs Pending' },
    { value: 'inprogress-pending', label: 'In Progress vs Pending' },
    { value: 'completed-cancelled', label: 'Completed vs Cancelled' },
    { value: 'pending-cancelled', label: 'Pending vs Cancelled' },
    { value: 'completed-inprogress-pending', label: 'Completed vs In Progress vs Pending' },
    { value: 'completed-inprogress-pending-cancelled', label: 'Completed vs In Progress vs Pending vs Cancelled' },
  ];

  return (
    <div className={styles.chartsSection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Performance Analytics</h2>
        <p className={styles.sectionSubtitle}>Visual insights into your task performance and achievements</p>
      </div>
      <div className={styles.chartsGrid}>
        <ChartCard
          title="Task Activity Trend"
          subtitle="Hourly breakdown of task activity"
          selector={{
            value: chartSelections.taskTrend,
            onChange: (value) => onChartSelectionChange('taskTrend', value),
            options: taskTrendOptions,
          }}
        >
          <canvas ref={taskTrendChartRef}></canvas>
        </ChartCard>

        <ChartCard
          title="Task Status Distribution"
          subtitle="Breakdown of tasks by current status"
        >
          <canvas ref={statusChartRef}></canvas>
        </ChartCard>

        <ChartCard
          title="Task Comparison"
          subtitle="Compare different task statuses"
          selector={{
            value: chartSelections.taskRatio,
            onChange: (value) => onChartSelectionChange('taskRatio', value),
            options: taskRatioOptions,
          }}
        >
          <canvas ref={taskRatioChartRef}></canvas>
        </ChartCard>

        <ChartCard
          title="Points Earned Over Time"
          subtitle="Track your point accumulation trend"
        >
          <canvas ref={pointsChartRef}></canvas>
        </ChartCard>
      </div>
    </div>
  );
};

export default ChartsSection;

